import {
    AnyPcbBakedNode,
    BakedRulesFor,
    FootPrintPadHoleType,
    LayerOrientation,
    PcbBoardLayer,
    PcbBoardLayerMaterials,
    PcbBoardLayerType,
    PcbNodeTypes,
    PcbViaType,
    colors,
} from "@buildwithflux/core";
import {isCopperLayer, isCopperMaterial} from "@buildwithflux/models";

/**
 * Gets the color for a PCB layer based on the layer orientation, material, and type.
 *
 * @param pcbLayoutNodeLayers - Array of PCB board layers to search
 * @param nodeLayer - The layer orientation, UID, or name to match
 * @param material - The material type to match (optional)
 * @param type - The layer type to match (optional)
 * @returns The layer color if found, or a default copper color for top/bottom layers
 */
export function getLayerColor(
    pcbLayoutNodeLayers: PcbBoardLayer[],
    // TODO: feels like this should be a predicate function specified by caller
    // to determine the criteria for layer selection, rather than mixing types
    // to this extent
    nodeLayer: LayerOrientation | string | undefined,
    material: PcbBoardLayerMaterials | undefined,
    type: PcbBoardLayerType | undefined,
) {
    // TODO: don't want to perform a search for multiple layers of every pad - this should at least be memo'd
    const layer = pcbLayoutNodeLayers.find(
        (layer) =>
            layer.uid === nodeLayer ||
            layer.name === nodeLayer ||
            (layer.orientation === nodeLayer &&
                ((material && layer.material === material) ||
                    (material === "Copper" && isCopperLayer(layer)) ||
                    (type && layer.type === type))),
    );

    if (layer?.color) {
        return layer.color;
    } else {
        if (nodeLayer === LayerOrientation.top) {
            if (material && isCopperMaterial(material)) {
                return colors.canvas.pcb.layout.stackupLayerColors.topCopper;
            }
        } else if (nodeLayer === LayerOrientation.bottom) {
            if (material && isCopperMaterial(material)) {
                return colors.canvas.pcb.layout.stackupLayerColors.bottomCopper;
            }
        }
    }
}

/**
 * Checks if a PCB node is a through-hole component (plated through-hole pad or through-hole via).
 *
 * @param node - The PCB baked node to check
 * @returns true if the node is a through-hole component, false otherwise
 */
export function isThroughHole(node: AnyPcbBakedNode) {
    return (
        (node.type === PcbNodeTypes.pad &&
            node?.bakedRules?.hole?.holeType === FootPrintPadHoleType.platedThroughHole) ||
        (node.type === PcbNodeTypes.via && node?.bakedRules?.viaType === PcbViaType.throughHole)
    );
}

/**
 * Gets the shape fields (layer color) for a PCB node based on its baked rules.
 * NOTE: needs to be used with shallow comparison for proper memoization
 *
 * @param bakedRules - The baked rules for the node
 * @param topLevelLayoutBakedRules - The baked rules for the top-level layout (optional)
 * @param topLevelFootprintBakedRules - The baked rules for the top-level footprint (optional)
 * @returns The layer color for the node
 */
export function getShapeFields(
    // TODO: Change this to `CommonBakedRules`
    bakedRules: BakedRulesFor<PcbNodeTypes>,
    topLevelLayoutBakedRules: BakedRulesFor<PcbNodeTypes.layout> | undefined,
    topLevelFootprintBakedRules: BakedRulesFor<PcbNodeTypes.footprint> | undefined,
) {
    const layer = bakedRules?.layer;

    let layerColor;

    if (topLevelLayoutBakedRules) {
        const pcbLayoutNodeLayers = Object.values(topLevelLayoutBakedRules?.stackup || {});

        layerColor = getLayerColor(pcbLayoutNodeLayers, layer, undefined, "Overlay");
    } else if (topLevelFootprintBakedRules) {
        const pcbLayoutNodeLayers = Object.values(topLevelFootprintBakedRules?.stackup || {});

        layerColor = getLayerColor(pcbLayoutNodeLayers, layer, undefined, "Overlay");
    } else {
        layerColor = getLayerColor([], layer, undefined, "Overlay");
    }

    return layerColor;
}
